import { genRandomHex } from '@1inch-community/core/random';
import { storage } from '@1inch-community/core/storage';
import { ChainId } from '@1inch-community/models';
import { EventEmitter } from 'eventemitter3';
import { fromEvent, merge, Subject, tap } from 'rxjs';
import { isAddressEqual } from 'viem';
const internalEvents = ['accountsChanged'];
export class MultiConnectProvider {
    constructor() {
        this.connectionUriLink$ = new Subject();
        this.storage = new Map();
        this.activeAddress = null;
        this.eventEmitter = new EventEmitter();
    }
    get chainId() {
        return this.signer()?.chainId ?? ChainId.eth;
    }
    signer(address = this.activeAddress) {
        if (address && this.storage.has(address)) {
            return this.storage.get(address)?.provider ?? null;
        }
        return null;
    }
    async connect(opts) {
        const persistStorePrefix = genRandomHex(10);
        try {
            const provider = await makeProvider(persistStorePrefix, opts);
            const subscription = this.listenEvents(provider);
            const { topic } = await provider.signer.client.core.pairing.create();
            let originalUri = '';
            provider.signer.once('display_uri', (uri) => {
                originalUri = uri;
                this.connectionUriLink$.next(uri);
            });
            await provider.connect({
                pairingTopic: topic,
            });
            const address = provider.accounts[0];
            if (this.storage.has(address)) {
                return;
            }
            const storage = {
                provider: provider,
                topic: topic,
                uri: originalUri,
                address: address,
                persistStorePrefix,
                subscription,
            };
            this.storage.set(address, storage);
            this.activeAddress = address;
            this.updatePersist();
            this.eventEmitter.emit('accountsChanged', this.getAddresses());
        }
        catch (error) {
            await dropStorage(persistStorePrefix);
            throw error;
        }
    }
    async restoreConnect() {
        const persistData = this.getPersistData();
        for (const data of persistData) {
            try {
                const provider = await makeProvider(data.persistStorePrefix);
                const address = provider.accounts[0];
                if (!isAddressEqual(address, data.address)) {
                    await dropStorage(data.persistStorePrefix);
                    continue;
                }
                const subscription = this.listenEvents(provider);
                this.storage.set(address, {
                    provider: provider,
                    uri: data.uri,
                    topic: data.topic,
                    address: data.address,
                    persistStorePrefix: data.persistStorePrefix,
                    subscription,
                });
                if (data.isActive) {
                    this.activeAddress = address;
                }
            }
            catch (error) {
                await dropStorage(data.persistStorePrefix);
                throw error;
            }
        }
        this.eventEmitter.emit('accountsChanged', this.getAddresses());
    }
    setActiveAddress(address) {
        this.activeAddress = address;
        this.eventEmitter.emit('accountsChanged', this.getAddresses());
    }
    async disconnectAll() {
        for (const address of this.storage.keys()) {
            await this.signer(address)
                ?.disconnect()
                .catch(() => {
                /* ignore */
            });
        }
        this.storage.clear();
        this.activeAddress = null;
        this.setActiveAddress(null);
        this.updatePersist();
    }
    async disconnect(address) {
        if (!address) {
            await this.disconnectAll();
        }
        else {
            this.storage.delete(address);
            await this.signer(address)
                ?.disconnect()
                .catch(() => {
                /* ignore */
            });
            if (this.activeAddress === address) {
                this.activeAddress = null;
                this.setActiveAddress(this.getAddresses()[0] ?? null);
            }
        }
        this.updatePersist();
    }
    isConnected() {
        return this.activeAddress !== null;
    }
    async request(args) {
        const method = args.method;
        if (method === 'eth_requestAccounts' || method === 'eth_accounts') {
            return this.getAddresses();
        }
        return (await this.signer()?.request(args)) ?? null;
    }
    async enable() {
        return this.getAddresses();
    }
    on(event, listener) {
        if (internalEvents.includes(event)) {
            this.eventEmitter.on(event, listener);
            return;
        }
        this.signer()?.on(event, listener);
    }
    removeListener(event, listener) {
        if (internalEvents.includes(event)) {
            this.eventEmitter.removeListener(event, listener);
            return;
        }
        this.signer()?.removeListener(event, listener);
    }
    updatePersist() {
        const persistStorePrefixSet = new Set();
        const data = [];
        this.storage.forEach(({ uri, topic, address, persistStorePrefix }) => {
            persistStorePrefixSet.add(persistStorePrefix);
            data.push({
                uri,
                topic,
                address,
                persistStorePrefix,
                isActive: !!this.activeAddress && isAddressEqual(this.activeAddress, address),
            });
        });
        this.cleanOldStorage().catch();
        storage.set('wc2_persist', data);
    }
    getPersistData() {
        return storage.get('wc2_persist', JSON.parse) ?? [];
    }
    getAddresses() {
        const addresses = new Set();
        this.activeAddress && addresses.add(this.activeAddress);
        this.storage.forEach((value) => {
            addresses.add(value.address);
        });
        return [...addresses];
    }
    listenEvents(provider) {
        let address;
        return merge(fromEvent(provider, 'disconnect').pipe(tap(() => {
            this.storage.forEach((value) => {
                if (value.provider !== provider)
                    return;
                address = value.address;
            });
            if (address && this.storage.has(address)) {
                const store = this.storage.get(address);
                this.storage.delete(address);
                store.subscription.unsubscribe();
                if (this.activeAddress === address) {
                    const nextActiveAddressStore = [...this.storage.values()][0];
                    this.activeAddress = nextActiveAddressStore?.address ?? null;
                }
                this.eventEmitter.emit('accountsChanged', this.getAddresses());
                this.updatePersist();
            }
        }))).subscribe();
    }
    async cleanOldStorage() {
        const databasesList = await indexedDB.databases();
        const dbNameSet = new Set();
        for (const store of this.storage.values()) {
            const dbName = await getDatabaseName(store.persistStorePrefix);
            dbNameSet.add(dbName);
        }
        for (const db of databasesList) {
            if (db.name && db.name.startsWith('wallet-connect') && !dbNameSet.has(db.name)) {
                await dropStorageByName(db.name);
            }
        }
    }
}
async function makeProvider(persistStorePrefix, opts) {
    const options = await import('./wallet-connect-init-options').then((m) => m.options());
    const { EthereumProvider } = await import('./ethereum-provider');
    const updatedOptions = opts && typeof opts === 'object' ? { ...options, ...opts } : options;
    return await EthereumProvider.initProvider(updatedOptions, persistStorePrefix);
}
async function dropStorageByName(name) {
    const { WalletConnectStorage } = await import('./ethereum-provider');
    await WalletConnectStorage.dropStorageByName(name);
}
async function getDatabaseName(name) {
    const { WalletConnectStorage } = await import('./ethereum-provider');
    return await WalletConnectStorage.getDatabaseName(name);
}
async function dropStorage(name) {
    const { WalletConnectStorage } = await import('./ethereum-provider');
    return await WalletConnectStorage.dropStorage(name);
}
//# sourceMappingURL=multi-connect-provider.js.map